import { useState } from 'react';
import { api } from '../api';
import { HOSTS, plural } from '../format';
import { useApp } from '../store';
import { btn, mono } from '../ui';

/** Плавающая панель массовых действий — появляется, как только выбран хост. */
export function BulkBar() {
  const app = useApp();
  const [confirmReboot, setConfirmReboot] = useState(false);

  if (!app.selCount) return null;

  const selWord = plural(app.selCount, HOSTS).replace(/^\d+\s/, '') + ' выбрано';

  const flat = {
    ...btn,
    padding: '7px 13px',
    borderRadius: 99,
    fontSize: 12.5,
    whiteSpace: 'nowrap' as const,
    color: 'var(--ink)'
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 22,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          padding: '11px 14px',
          borderRadius: 99,
          background: 'var(--bar)',
          border: '1px solid var(--line2)',
          boxShadow: '0 18px 44px rgba(0,0,0,.5)'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 9, paddingRight: 6 }}>
          <span
            style={{
              minWidth: 26,
              height: 26,
              padding: '0 8px',
              boxSizing: 'border-box',
              borderRadius: 99,
              background: 'var(--acc)',
              color: 'var(--accInk)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12.5,
              fontWeight: 700
            }}
          >
            {app.selCount}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>{selWord}</span>
        </span>

        <span style={{ width: 1, height: 22, background: 'var(--line2)' }} />

        <button className="hov" style={flat} onClick={() => app.go('commands')}>
          Команда
        </button>
        <button className="hov" style={flat} onClick={() => app.go('files')}>
          Файлы
        </button>
        <button className="hov" style={flat} onClick={() => app.go('rdp')}>
          RDP-сетка
        </button>
        <button className="hov" style={flat} onClick={() => void app.runSpeed()}>
          Speedtest
        </button>
        <button
          className="hovBad"
          onClick={() => setConfirmReboot(true)}
          style={{
            ...btn,
            padding: '7px 14px',
            borderRadius: 99,
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            background: 'var(--badSoft)',
            color: 'var(--bad)'
          }}
        >
          Перезагрузить
        </button>
        <button
          className="hovInk"
          onClick={app.clearSel}
          style={{
            ...btn,
            padding: '7px 11px',
            borderRadius: 99,
            fontSize: 12.5,
            whiteSpace: 'nowrap',
            color: 'var(--ink3)'
          }}
        >
          Снять
        </button>
      </div>

      {confirmReboot && (
        <Confirm
          title={`Перезагрузить ${plural(app.selCount, HOSTS)}?`}
          detail="Перезагрузка начнётся немедленно, окно обслуживания не проверяется."
          hosts={app.selected.slice(0, 6).map(h => h.name)}
          more={Math.max(0, app.selCount - 6)}
          onCancel={() => setConfirmReboot(false)}
          onConfirm={() => {
            setConfirmReboot(false);
            void app.runReboot();
          }}
        />
      )}
    </>
  );
}

/**
 * Подтверждение необратимого действия.
 *
 * Команды в этой консоли запускаются сразу, без диалогов — так просил
 * заказчик. Но массовая перезагрузка сотни продовых серверов от случайного
 * клика — это другое, поэтому здесь спрашиваем.
 */
export function Confirm({
  title, detail, hosts, more, onCancel, onConfirm
}: {
  title: string;
  detail: string;
  hosts: string[];
  more: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(0,0,0,.55)',
        backdropFilter: 'blur(3px)'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(460px, 100%)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: '22px 24px',
          borderRadius: 'var(--r)',
          background: 'var(--bar)',
          border: '1px solid var(--line2)',
          boxShadow: '0 24px 60px rgba(0,0,0,.6)'
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
        <span style={{ fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.55 }}>{detail}</span>
        <div
          style={{
            ...mono,
            fontSize: 11.5,
            color: 'var(--ink3)',
            background: 'var(--panel2)',
            borderRadius: 'var(--rs)',
            padding: '10px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          {hosts.map(h => (
            <span key={h}>{h}</span>
          ))}
          {more > 0 && <span style={{ color: 'var(--ink4)' }}>… и ещё {more}</span>}
        </div>
        <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
          <button
            className="hov"
            onClick={onCancel}
            style={{
              ...btn,
              padding: '9px 15px',
              borderRadius: 'var(--rs)',
              border: '1px solid var(--line)',
              fontSize: 12.5,
              color: 'var(--ink2)'
            }}
          >
            Отмена
          </button>
          <button
            className="bright"
            onClick={onConfirm}
            style={{
              ...btn,
              padding: '9px 17px',
              borderRadius: 'var(--rs)',
              background: 'var(--bad)',
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 600
            }}
          >
            Перезагрузить
          </button>
        </div>
      </div>
    </div>
  );
}

/** Всплывающее сообщение о ходе прогона. */
export function Toast() {
  const app = useApp();
  if (!app.toast) return null;
  const running = app.job?.state === 'running';
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 88,
        right: 26,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        borderRadius: 'var(--r)',
        background: 'var(--bar)',
        border: '1px solid var(--line2)',
        boxShadow: '0 18px 44px rgba(0,0,0,.5)',
        maxWidth: 400
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'var(--acc)',
          animation: 'blip 1.2s infinite'
        }}
      />
      <span style={{ ...mono, fontSize: 12 }}>{app.toast}</span>
      {running && (
        <button
          onClick={() => void app.cancelRun()}
          style={{ ...btn, fontSize: 11.5, color: 'var(--bad)', whiteSpace: 'nowrap' }}
        >
          Отменить
        </button>
      )}
    </div>
  );
}

/** Кнопка скачивания установщика агента — живёт на пустом парке. */
export function InstallerHint() {
  const app = useApp();
  return (
    <button
      className="hov"
      onClick={() => void api.downloadInstaller().catch(e => app.notify(String(e.message ?? e)))}
      style={{
        ...btn,
        padding: '9px 15px',
        borderRadius: 'var(--rs)',
        border: '1px solid var(--line)',
        fontSize: 12.5,
        color: 'var(--ink2)'
      }}
    >
      Скачать установщик агента
    </button>
  );
}
